import { NgTemplateOutlet } from '@angular/common';
import { type AfterContentInit, Component, effect, EventEmitter, inject, Input, input, Output, ViewChild } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton, MatButtonModule } from '@angular/material/button';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { DateRange } from '@angular/material/datepicker';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormField, MatOption, MatSelect, MatSelectChange, MatSelectModule, MatSuffix } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { SessionStorageService } from '@core';
import { SortPipe } from '@core';
import { IncidentTabStatusFilter } from '@models';
import { DatePickerComponent, MultipleSelectComponent, SearchInputComponent } from '@ui';
import { sub } from 'date-fns';

import { IncidentsCriteriaStore, type IncidentsFilterCriteria } from '../../../state/incidents-criteria.store';

@Component({
  imports: [
    DatePickerComponent,
    FormsModule,
    MatButton,
    MatButtonModule,
    MatButtonToggle,
    MatButtonToggleGroup,
    MatFormField,
    MatIcon,
    MatProgressSpinner,
    MatSelectModule,
    MatSuffix,
    MatTooltip,
    MultipleSelectComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    SearchInputComponent,
    SortPipe,
  ],
  selector: 'avs-incidents-v2-table-desktop-filters',
  standalone: true,
  templateUrl: './incidents-v2-table-desktop-filters.component.html',
})
export class IncidentsV2TableDesktopFiltersComponent implements AfterContentInit {
  @Input() filtersEnabled = false;
  @Input() showColumnSelect = true;
  @Input() dropdownFilters = true;
  @Input() resolvedIncidentsLoading = false;
  @Input() tableId!: string;
  showLocationFilters = input(true);
  @ViewChild('columnSelectRef') columnSelectRef!: MatSelect;
  /* Emits the current selection whenever a control changes; only the store-backed main list listens. */
  @Output() filterCriteriaChanged = new EventEmitter<IncidentsFilterCriteria>();
  incidentTabStatusFilterEnum = IncidentTabStatusFilter;

  protected minDate = sub(new Date(), { months: 1 });

  protected criteriaStore = inject(IncidentsCriteriaStore);
  /** UI-only widget state — not shared cross-component, so it stays local rather than living in `IncidentsCriteriaStore`. */
  protected columnSelectControl = new FormControl<string[]>([]);
  private sessionStorageService = inject(SessionStorageService);

  constructor() {
    /*
     * Resyncs the column-picker's own selection whenever the store's
     * `displayedColumns` changes because of a tab switch (the 'resolved'
     * column toggling in/out) rather than a direct user pick on this control.
     */
    effect(() => {
      const columns = this.criteriaStore.displayedColumns().filter(column => column !== 'index' && column !== 'ticketNumber');

      if (JSON.stringify(this.columnSelectControl.value) !== JSON.stringify(columns)) {
        this.columnSelectControl.setValue(columns);
      }
    });
  }

  columnListUpdated($event: MatSelectChange) {
    this.columnSelectControl.setValue($event.value);
    this.sessionStorageService.saveColumnsSettingsToSessionStorage(this.tableId, $event.value);
    this.criteriaStore.setDisplayedColumns($event.value);
  }

  selectAllColumns() {
    this.columnSelectRef.options.forEach((item: MatOption) => item.select());
  }

  ngAfterContentInit() {
    const columns = this.sessionStorageService.getColumns(this.tableId);

    if (columns?.length) {
      this.columnListUpdated({ value: columns } as MatSelectChange);
    }
  }

  protected onTabChange(tab: IncidentTabStatusFilter) {
    this.criteriaStore.setTab(tab);
    this.emitFilterCriteria();
  }

  protected onSeverityChange(value: string[]) {
    this.criteriaStore.setSeverityFilter(value);
    this.emitFilterCriteria();
  }

  protected onDeviceTypeChange(value: string[]) {
    this.criteriaStore.setDeviceTypeValue(value);
    this.emitFilterCriteria();
  }

  protected onBuildingChange(value: string) {
    this.criteriaStore.selectBuilding(value);
    this.emitFilterCriteria();
  }

  protected onFloorChange(value: string) {
    this.criteriaStore.selectFloor(value);
    this.emitFilterCriteria();
  }

  protected onRoomChange(value: string) {
    this.criteriaStore.selectRoom(value);
    this.emitFilterCriteria();
  }

  protected onSearchChange(value: string) {
    this.criteriaStore.setSearch(value);
    this.emitFilterCriteria();
  }

  protected onClearOpenFilters() {
    this.criteriaStore.clearOpenFilters();
    this.emitFilterCriteria();
  }

  protected onResolvedDeviceTypeChange(value: string[]) {
    this.criteriaStore.setResolvedDeviceTypeValue(value);
    this.emitFilterCriteria();
  }

  protected onResolvedBuildingChange(value: string) {
    this.criteriaStore.selectResolvedBuilding(value);
    this.emitFilterCriteria();
  }

  protected onResolvedFloorChange(value: string) {
    this.criteriaStore.selectResolvedFloor(value);
    this.emitFilterCriteria();
  }

  protected onResolvedRoomChange(value: string) {
    this.criteriaStore.selectResolvedRoom(value);
    this.emitFilterCriteria();
  }

  protected onClearResolvedFilters() {
    this.criteriaStore.clearResolvedFilters();
    this.emitFilterCriteria();
  }

  protected onResolvedDateRangeChange(range: DateRange<Date>) {
    this.criteriaStore.setResolvedDateRange(range);
  }

  private emitFilterCriteria() {
    const isOpen = this.criteriaStore.tab() === this.incidentTabStatusFilterEnum.OPEN;

    /* Fields of the other tab are set to `undefined` explicitly: `setSelectedFilters` merges, so omitted keys would leak across tabs. */
    this.filterCriteriaChanged.emit(
      isOpen
        ? {
          deviceTypeId: this.criteriaStore.deviceTypeIds(),
          endDateTime: undefined,
          locationId: this.criteriaStore.selectedBuildingId(),
          query: this.criteriaStore.search() || undefined,
          severity: this.criteriaStore.severityFilter(),
          spaceId: this.criteriaStore.selectedRoomId() || this.criteriaStore.selectedFloorId(),
          startDateTime: undefined,
        }
        : {
          deviceTypeId: this.criteriaStore.resolvedIncidentsDeviceTypeIds(),
          /*
           * Re-assert the already-selected date range every time, rather than
           * omitting it — otherwise it would keep whatever an Open-tab emission
           * last set it to (`undefined`, see above) instead of the value the
           * user actually picked.
           */
          endDateTime: this.criteriaStore.resolvedDates().endDateTime || undefined,
          locationId: this.findOptionId(
            this.criteriaStore.resolvedIncidentsBuildingOptions(),
            this.criteriaStore.resolvedIncidentsSelectedBuilding(),
          ),
          query: undefined,
          severity: undefined,
          spaceId:
            this.findOptionId(
              this.criteriaStore.resolvedIncidentsRoomOptions(),
              this.criteriaStore.resolvedIncidentsSelectedRoom(),
            ) ||
            this.findOptionId(
              this.criteriaStore.resolvedIncidentsFloorOptions(),
              this.criteriaStore.resolvedIncidentsSelectedFloor(),
            ),
          startDateTime: this.criteriaStore.resolvedDates().startDateTime || undefined,
        },
    );
  }

  private findOptionId(options: { id?: string; value: string; }[], value: string) {
    return options.find(option => option.value === value)?.id;
  }
}
